import { supabase } from '../lib/supabase';

// ── Types ────────────────────────────────────────────────────────────

export interface AchievementUnlockResult {
  id: string;
  title: string;
  description: string;
  category: string;
  rarity: string;
  icon: string;
  tier: 'common' | 'rare' | 'epic' | 'legendary';
  rarityScalar: number;
  isHidden: boolean;
  xpReward: number;
  unlockedAt: string;
}

export interface AchievementContext {
  currentLevel?: number;
  totalXP?: number;
  streakCount?: number;
  formQuality?: number;
  exerciseElo?: Record<string, number>;
  workoutCount?: number;
  totalSets?: number;
  followsCount?: number;
  monthlyChallengeWins?: number;
}

const RARITY_XP: Record<string, number> = {
  Bronze: 10, Silver: 25, Gold: 50, Platinum: 100, Legendary: 250,
  common: 10, rare: 25, epic: 50, legendary: 100,
};

// ── checkAchievementUnlocks (context-based) ──────────────────────────

export async function checkAchievementUnlocks(
  userId: string,
  context?: AchievementContext,
): Promise<AchievementUnlockResult[]> {
  try {
    // Try achievement_catalog first, fall back to achievement_definitions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let catalog: any[] = [];
    const { data: cat1 } = await supabase.from('achievement_catalog').select('*');
    if (cat1 && cat1.length > 0) {
      catalog = cat1;
    } else {
      const { data: cat2 } = await supabase.from('achievement_definitions').select('*');
      catalog = cat2 ?? [];
    }

    if (!catalog.length) return [];

    // Fetch already-earned
    const { data: earned } = await supabase
      .from('achievements')
      .select('achievement_id, definition_id')
      .eq('user_id', userId);

    const earnedIds = new Set<string>();
    for (const e of earned ?? []) {
      if (e.achievement_id) earnedIds.add(e.achievement_id);
      if (e.definition_id) earnedIds.add(e.definition_id);
    }

    // Gather stats if no context provided
    const stats = context ?? await gatherUserStats(userId);

    const unlocked: AchievementUnlockResult[] = [];
    const now = new Date().toISOString();

    for (const entry of catalog) {
      const entryId = entry.id;
      if (earnedIds.has(entryId)) continue;

      const condition = entry.trigger_condition ?? {};
      if (!evaluateCondition(condition, stats)) continue;

      const tier = entry.tier ?? entry.rarity ?? 'common';
      const xpReward = RARITY_XP[tier] ?? 10;

      // Insert unlock
      await supabase.from('achievements').insert({
        user_id: userId,
        achievement_id: entryId,
        definition_id: entryId,
        title: entry.title,
        description: entry.description,
        category: entry.category ?? '',
        rarity: tier,
        tier,
        rarity_scalar: entry.rarity_scalar ?? 1,
        date_earned: now,
        auto_detected: true,
        reveal_animation_complete: false,
        progress_percent: 100,
        unlocked_at: now,
      });

      unlocked.push({
        id: entryId,
        title: entry.title,
        description: entry.description ?? '',
        category: entry.category ?? '',
        rarity: tier,
        icon: entry.icon ?? '',
        tier: tier as AchievementUnlockResult['tier'],
        rarityScalar: entry.rarity_scalar ?? 1,
        isHidden: entry.hidden ?? false,
        xpReward,
        unlockedAt: now,
      });
    }

    return unlocked;
  } catch (err) {
    console.error('[achievementEngine] checkAchievementUnlocks failed:', err);
    return [];
  }
}

// ── checkAutoAchievements (alias) ────────────────────────────────────

export const checkAutoAchievements = checkAchievementUnlocks;

// ── getAllAchievements ────────────────────────────────────────────────

export async function getAllAchievements(
  userId: string,
): Promise<Array<AchievementUnlockResult & { locked: boolean; progressPercent: number }>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let catalog: any[] = [];
    const { data: cat1 } = await supabase.from('achievement_catalog').select('*');
    if (cat1 && cat1.length > 0) catalog = cat1;
    else {
      const { data: cat2 } = await supabase.from('achievement_definitions').select('*');
      catalog = cat2 ?? [];
    }

    const { data: earned } = await supabase
      .from('achievements')
      .select('*')
      .eq('user_id', userId);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const earnedMap = new Map<string, any>();
    for (const e of earned ?? []) {
      if (e.achievement_id) earnedMap.set(e.achievement_id, e);
      if (e.definition_id) earnedMap.set(e.definition_id, e);
    }

    return catalog.map(entry => {
      const unlock = earnedMap.get(entry.id);
      const tier = entry.tier ?? entry.rarity ?? 'common';
      return {
        id: entry.id,
        title: entry.title,
        description: entry.description ?? '',
        category: entry.category ?? '',
        rarity: tier,
        icon: entry.icon ?? '',
        tier: tier as AchievementUnlockResult['tier'],
        rarityScalar: entry.rarity_scalar ?? 1,
        isHidden: entry.hidden ?? false,
        xpReward: RARITY_XP[tier] ?? 10,
        unlockedAt: unlock?.unlocked_at ?? unlock?.date_earned ?? '',
        locked: !unlock,
        progressPercent: unlock?.progress_percent ?? 0,
      };
    });
  } catch (err) {
    console.error('[achievementEngine] getAllAchievements failed:', err);
    return [];
  }
}

// ── Stats gathering ──────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function gatherUserStats(userId: string): Promise<Record<string, any>> {
  const [workouts, analyses, skills, profile] = await Promise.all([
    supabase.from('workout_logs').select('id', { count: 'exact', head: true }).eq('user_id', userId),
    supabase.from('analyses').select('overall_score').eq('user_id', userId).order('created_at', { ascending: false }).limit(1),
    supabase.from('user_skills').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('unlocked', true),
    supabase.from('profiles').select('streak_count, longest_streak, total_xp, current_level').eq('id', userId).single(),
  ]);

  return {
    workout_count: workouts.count ?? 0,
    physique_score: analyses.data?.[0]?.overall_score ?? 0,
    skill_unlock_count: skills.count ?? 0,
    streak: profile.data?.streak_count ?? 0,
    longest_streak: profile.data?.longest_streak ?? 0,
    currentLevel: profile.data?.current_level ?? 1,
    totalXP: profile.data?.total_xp ?? 0,
    streakCount: profile.data?.streak_count ?? 0,
  };
}

// ── Condition evaluation ─────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function evaluateCondition(condition: Record<string, any>, stats: Record<string, any>): boolean {
  if (!condition?.type) return false;
  const { type, threshold } = condition;

  switch (type) {
    case 'workout_count': return (stats.workout_count ?? stats.workoutCount ?? 0) >= threshold;
    case 'analysis_count': return (stats.analysis_count ?? 0) >= threshold;
    case 'physique_score': return (stats.physique_score ?? stats.formQuality ?? 0) >= threshold;
    case 'meal_count': return (stats.meal_count ?? 0) >= threshold;
    case 'skill_unlock_count': return (stats.skill_unlock_count ?? 0) >= threshold;
    case 'post_count': return (stats.post_count ?? stats.followsCount ?? 0) >= threshold;
    case 'streak':
    case 'streak_days':
    case 'consecutive_days':
      return (stats.streak ?? stats.streakCount ?? 0) >= threshold;
    case 'level_reached': return (stats.currentLevel ?? 0) >= threshold;
    case 'xp_total': return (stats.totalXP ?? 0) >= threshold;
    case 'elo_rating': {
      const elos = stats.exerciseElo ?? {};
      return Object.values(elos).some(e => (e as number) >= threshold);
    }
    case 'follows_count': return (stats.followsCount ?? 0) >= threshold;
    case 'challenge_wins': return (stats.monthlyChallengeWins ?? 0) >= threshold;
    default: return false;
  }
}
